import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { useT } from '@/lib/i18n';

import { forgotPassword } from '../api/auth';

function forgotSchema(t: ReturnType<typeof useT>) {
  return z.object({
    email: z.string().min(1, t('auth.common.email_required')).email(t('auth.common.email_invalid')),
  });
}

type FormValues = z.infer<ReturnType<typeof forgotSchema>>;

export function ForgotPasswordPage() {
  const t = useT();
  const [sent, setSent] = useState(false);
  const schema = useMemo(() => forgotSchema(t), [t]);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    await forgotPassword(values.email);
    // The server answers identically for unknown addresses, and so do we.
    setSent(true);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight text-ink">{t('auth.forgot.title')}</h1>

      {sent ? (
        <Alert tone="success" className="mt-5">
          {t('auth.forgot.sent')}
        </Alert>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="mt-5 space-y-4">
          <p className="text-sm text-muted">
            {t('auth.forgot.intro')}
          </p>
          <TextField
            label={t('auth.common.email')}
            type="email"
            autoComplete="email"
            error={errors.email?.message}
            {...register('email')}
          />
          <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t('auth.common.sending') : t('auth.forgot.submit')}
          </Button>
        </form>
      )}

      <p className="mt-6 text-center text-sm text-muted">
        <Link to="/login" className="font-semibold text-brand-600 hover:underline">
          {t('auth.common.back_to_sign_in')}
        </Link>
      </p>
    </div>
  );
}
